import { useRef, useEffect, useCallback } from 'react'
import {
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { toast } from 'react-toastify'

import { AgGridTooltip } from '../components'

const AgGridActionCellRenderer = (props) => {
  const {
    registerRowDragger,
    generateDummyRowData,
    editColKey,
    dragEnabled = true,
    editEnabled = true,
    addEnabled = true,
    removeEnabled = true,
  } = props
  const dragRef = useRef(null)
  const tooltipRef = useRef(null)

  // Register row dragger
  useEffect(() => {
    registerRowDragger(dragRef.current)
  })

  /**
   * Edit row
   */
  const editRow = useCallback((params) => {
    params.api.startEditingCell({
      rowIndex: params.rowIndex,
      // Place the cursor in the specified colKey column
      colKey: params.editColKey ?? '',
    })
  }, [])

  /**
   * Add row
   */
  const addRow = useCallback(
    (params) => {
      const dummyRowData = generateDummyRowData()
      const atIndex = params ? params.rowIndex + 1 : 0
      const res = params.api.applyTransaction({
        add: [dummyRowData],
        addIndex: atIndex,
      })

      // Turn on row editing after the animation is finished (~250ms)
      setTimeout(() => {
        editRow({
          ...params,
          rowIndex: res.add[0]?.rowIndex,
          editColKey: editColKey ?? '',
        })
      }, 250)
    },
    [editRow, editColKey, generateDummyRowData]
  )

  /**
   * Remove row
   */
  const removeRow = useCallback((params) => {
    if (!params?.data?.id && !params?.data?.postId) {
      toast.error(`Unable to remove this item. ID is undefined.`)
      return
    }

    if (params?.data?.id) {
      params.api.applyTransaction({
        remove: [{ id: params?.data?.id }],
      })
    } else if (params?.data?.postId) {
      params.api.applyTransaction({
        remove: [{ postId: params?.data?.postId }],
      })
    } else {
      // Unreachable
    }
  }, [])

  /**
   * Show tooltip on mouse enter
   */
  const handleMouseEnter = useCallback((e, text = '') => {
    const el = e.currentTarget
    const rect = el.getBoundingClientRect()

    tooltipRef.current.style.top = `${
      rect.top +
      window.pageYOffset -
      // FIXME: This is very hacky. Find out why button with svg returns
      // different offsetHeight than the text
      el.offsetHeight * (text !== 'Move' ? 1.5 : 1)
    }px`
    tooltipRef.current.style.left = `${
      rect.left + window.pageXOffset - el.offsetWidth / 2 - rect.width / 2
    }px`
    tooltipRef.current.style.opacity = 1
    tooltipRef.current.textContent = text
  }, [])

  /**
   * Hide tooltip on mouse leave
   */
  const handleMouseLeave = useCallback(() => {
    tooltipRef.current.textContent = ''
    tooltipRef.current.style.opacity = 0
    tooltipRef.current.style.top = '-10000px'
    tooltipRef.current.style.left = '-10000px'
  }, [])

  return (
    <>
      {/** Tooltip for action buttons */}
      <AgGridTooltip ref={tooltipRef} />

      <div className="flex h-full items-center gap-x-1">
        {/** Show drag (move) button */}
        {dragEnabled && (
          <button
            ref={dragRef}
            className="cursor-move [&_.ag-icon]:!block"
            onMouseEnter={(e) => handleMouseEnter(e, 'Move')}
            onMouseLeave={(e) => handleMouseLeave(e)}
          >
            <span
              className="ag-icon ag-icon-grip"
              unselectable="on"
              role="presentation"
            ></span>
          </button>
        )}

        {/** Show edit button */}
        {editEnabled && (
          <button
            onClick={() => editRow(props)}
            onMouseEnter={(e) => handleMouseEnter(e, 'Edit')}
            onMouseLeave={(e) => handleMouseLeave(e)}
          >
            <PencilSquareIcon className="h-[12px] w-[12px] stroke-blue-500 stroke-[2]" />
          </button>
        )}

        {/** Show add button */}
        {addEnabled && (
          <button
            onClick={() => addRow(props)}
            onMouseEnter={(e) => handleMouseEnter(e, 'Add')}
            onMouseLeave={(e) => handleMouseLeave(e)}
          >
            <PlusIcon className="h-[12px] w-[12px] stroke-green stroke-[3]" />
          </button>
        )}

        {/** Show remove button */}
        {removeEnabled && (
          <button
            onClick={() => removeRow(props)}
            onMouseEnter={(e) => handleMouseEnter(e, 'Remove')}
            onMouseLeave={(e) => handleMouseLeave(e)}
          >
            <TrashIcon className="h-[12px] w-[12px] stroke-red-500 stroke-[2]" />
          </button>
        )}
      </div>
    </>
  )
}

export default AgGridActionCellRenderer
